import { useState, useEffect } from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Flex, Skeleton, Tooltip } from "antd"
import { PhoneOutlined, MailOutlined, MessageOutlined, SearchOutlined, ExclamationCircleFilled } from "@ant-design/icons"
import EmptyState from "components/common/EmptyState"
import styles from "../../index.module.css"

const CommTypeIcon = ({ type }) => {
  if (type === "call") return <PhoneOutlined className={styles.commIcon} />
  if (type === "sms") return <MessageOutlined className={styles.commIcon} />
  return <MailOutlined className={styles.commIcon} />
}

CommTypeIcon.propTypes = { type: PropTypes.string.isRequired }

// Placeholder rows mirroring the comm-item layout (avatar + two text lines)
const CommSkeleton = () => (
  <Flex vertical>
    {["a", "b", "c", "d"].map((placeholderKey) => (
      <Flex key={placeholderKey} align="flex-start" gap={12} className={styles.commItem}>
        <Skeleton.Avatar active size={40} shape="circle" />
        <Skeleton active title={{ width: "40%" }} paragraph={{ rows: 1, width: "70%" }} />
      </Flex>
    ))}
  </Flex>
)

const CommunicationHistoryPanel = ({ communications = [], loading = false }) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [query, setQuery] = useState("")

  // Debounce the applied query so the list only filters after typing settles; while the
  // input and the applied query differ, a search is pending and we show a spinner.
  useEffect(() => {
    const timeout = setTimeout(() => setQuery(search.trim().toLowerCase()), 300)
    return () => clearTimeout(timeout)
  }, [search])

  const isSearching = search.trim().toLowerCase() !== query
  const filtered = communications.filter(
    (item) =>
      (item.contactName ?? "").toLowerCase().includes(query) ||
      (item.label ?? "").toLowerCase().includes(query) ||
      (item.contactInfo ?? "").toLowerCase().includes(query)
  )

  return (
    <Flex vertical gap={12} className={styles.commPanel}>
      <span className={styles.commPanelTitle}>{t("booking.reservation_detail.communication_history")}</span>
      <Flex align="center" gap={8} className={styles.commSearch}>
        <SearchOutlined className={styles.commSearchIcon} />
        <input
          type="text"
          placeholder={t("booking.reservation_detail.search")}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className={styles.commSearchInput}
        />
      </Flex>
      <Flex vertical className={styles.commList}>
        {((loading && communications.length === 0) || isSearching) && <CommSkeleton />}
        {!loading && !isSearching && communications.length === 0 && (
          <span className={styles.commDate}>{t("booking.reservation_detail.no_communications", "No communication history.")}</span>
        )}
        {!loading && !isSearching && communications.length > 0 && filtered.length === 0 && (
          <EmptyState
            title={t("booking.reservation_detail.no_comm_results_title", "No results found")}
            description={t("booking.reservation_detail.no_comm_results_desc", "No messages match your search.")}
          />
        )}
        {!isSearching && filtered.map((item) => (
          <Flex key={item.id} align="flex-start" gap={12} className={styles.commItem}>
            <div className={styles.commIconWrap}>
              <CommTypeIcon type={item.type} />
            </div>
            <Flex vertical gap={2} className={styles.commBody}>
              <Flex align="center" gap={6}>
                <span className={styles.commName}>{item.contactName ?? item.label}</span>
                {item.label && item.contactName && <span className={styles.commTypeLabel}>{item.label}</span>}
                {!item.contactName && (
                  <span className={styles.commTypeLabel}>
                    {item.type === "sms" ? t("booking.reservation_detail.sms") : t("booking.reservation_detail.email")}
                  </span>
                )}
                {item.status === "failed" && (
                  <Tooltip title={t("booking.reservation_detail.comm_failed", "Delivery failed — the recipient did not get this message.")}>
                    <ExclamationCircleFilled className={styles.commFailedIcon} />
                  </Tooltip>
                )}
              </Flex>
              <span className={styles.commContact}>{item.contactInfo}</span>
              {item.sentBy && (
                <span className={styles.commSentBy}>{t("booking.reservation_detail.sent_by", { name: item.sentBy, defaultValue: `Sent by ${item.sentBy}` })}</span>
              )}
              <span className={styles.commDate}>{item.date}</span>
            </Flex>
          </Flex>
        ))}
      </Flex>
    </Flex>
  )
}

CommunicationHistoryPanel.propTypes = {
  communications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["call", "sms", "email", "in_app", "push"]).isRequired,
      contactName: PropTypes.string,
      label: PropTypes.string,
      contactInfo: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      status: PropTypes.string,
      sentBy: PropTypes.string
    })
  ),
  loading: PropTypes.bool
}

export default CommunicationHistoryPanel
